import React, { useEffect, useMemo, useRef } from "react";
import { Box, GlobalStyles } from "@mui/material";
import TheBprLogo from "./TheBprLogo";
import { LOGIN_AGENTS, svgSize } from "./loginAgents";

interface LoginIntroAnimationProps {
  // Live roller-item DOM nodes (FLIP targets) supplied by the shell.
  getRollerItems: () => (HTMLDivElement | null)[];
  // Fired when the app chrome (sidebar + card) should slide in, before the FLIP.
  onReveal: () => void;
  // Fired once the stage has finished and can be unmounted.
  onComplete: () => void;
}

// All keyframes/classes are global and prefixed `bsli-` (login intro) because ripples and
// FLIP clones are created imperatively and can't carry Emotion-scoped classes.
const introStyles = (
  <GlobalStyles
    styles={{
      "@keyframes bsli-dot-breathe": {
        "0%,100%": { boxShadow: "0 0 14px 5px rgba(70,160,235,0.6)" },
        "50%": { boxShadow: "0 0 32px 16px rgba(70,160,235,0.2)" },
      },
      "@keyframes bsli-ripple-out": {
        "0%": { transform: "translate(-50%,-50%) scale(0)", opacity: 1 },
        "80%": { opacity: 0.2 },
        "100%": { transform: "translate(-50%,-50%) scale(1)", opacity: 0 },
      },
      "@keyframes bsli-node-in": {
        "0%": { opacity: 0, transform: "translate(-50%,-50%) scale(0)" },
        "100%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
      },
      "@keyframes bsli-node-out": {
        "0%": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
        "100%": { opacity: 0, transform: "translate(-50%,-50%) scale(0.4)" },
      },
      ".bsli-dot": {
        position: "absolute", left: "50%", top: "50%", width: 10, height: 10,
        margin: "-5px 0 0 -5px", borderRadius: "50%", background: "#46A0EB",
        opacity: 0, transform: "scale(0)",
        transition: "opacity 400ms ease, transform 400ms cubic-bezier(0.34,1.4,0.64,1)",
      },
      ".bsli-dot.alive": { opacity: 1, transform: "scale(1)", animation: "bsli-dot-breathe 1.2s ease-in-out infinite" },
      ".bsli-dot.expand": {
        animation: "none",
        transition: "width 900ms cubic-bezier(0.4,0,0.2,1), height 900ms cubic-bezier(0.4,0,0.2,1), margin 900ms cubic-bezier(0.4,0,0.2,1), opacity 300ms ease 750ms, box-shadow 600ms ease",
        width: "300vmax", height: "300vmax", margin: "calc(-150vmax) 0 0 calc(-150vmax)",
        opacity: 0, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(70,160,235,0.20) 0%, rgba(23,120,199,0.07) 35%, transparent 65%)",
        boxShadow: "0 0 250px 100px rgba(70,160,235,0.10)",
      },
      ".bsli-ripple": {
        position: "absolute", left: "50%", top: "50%", borderRadius: "50%",
        border: "1px solid rgba(70,160,235,0.5)",
        transform: "translate(-50%,-50%) scale(0)", opacity: 0, pointerEvents: "none",
        animation: "bsli-ripple-out var(--dur,1400ms) cubic-bezier(0.15,0,0.6,1) forwards",
      },
      "#bsli-stage-logo": {
        position: "absolute", left: "50%", top: "50%", width: "min(300px,42vw)",
        transform: "translate(-50%,-50%) scale(0.75)", opacity: 0, pointerEvents: "none",
        transition: "opacity 600ms ease, transform 600ms cubic-bezier(0.34,1.15,0.64,1)",
      },
      "#bsli-stage-logo.show": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
      "#bsli-stage-logo.hide": { opacity: 0, transform: "translate(-50%,-53%) scale(0.85)", transition: "opacity 450ms ease, transform 450ms cubic-bezier(0.4,0,1,1)" },
      "#bsli-orbit": {
        position: "absolute", left: "50%", top: "50%", borderRadius: "50%",
        border: "1px dashed rgba(70,160,235,0.13)",
        transform: "translate(-50%,-50%) scale(0)", opacity: 0, pointerEvents: "none",
        transition: "transform 800ms cubic-bezier(0.34,1.1,0.64,1), opacity 500ms ease",
      },
      "#bsli-orbit.show": { opacity: 1, transform: "translate(-50%,-50%) scale(1)" },
      "#bsli-orbit.hide": { opacity: 0, transition: "opacity 350ms ease" },
      ".bsli-anode": {
        position: "absolute", display: "flex", flexDirection: "column", alignItems: "center",
        gap: "8px", pointerEvents: "none", opacity: 0, transform: "translate(-50%,-50%) scale(0)",
      },
      ".bsli-anode.show": { animation: "bsli-node-in 500ms cubic-bezier(0.34,1.25,0.64,1) forwards" },
      ".bsli-anode.hide": { animation: "bsli-node-out 360ms cubic-bezier(0.4,0,1,1) forwards" },
      ".bsli-anode-pip": { width: 6, height: 6, borderRadius: "50%", background: "#46A0EB", boxShadow: "0 0 8px 3px rgba(70,160,235,0.55)" },
      ".bsli-flip-clone": { position: "fixed", pointerEvents: "none", zIndex: 50, display: "flex", alignItems: "center", willChange: "transform, opacity" },
      ".bsli-flip-clone.flying": { transition: "transform 520ms cubic-bezier(0.34,1.1,0.64,1), opacity 200ms ease 420ms" },
    }}
  />
);

const LoginIntroAnimation: React.FC<LoginIntroAnimationProps> = ({ getRollerItems, onReveal, onComplete }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const dotRef = useRef<HTMLDivElement>(null);
  const ringsRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Geometry frozen at mount so node positions stay stable through the sequence.
  const geom = useMemo(() => {
    const W = window.innerWidth, H = window.innerHeight;
    const ORBIT_R = Math.min(W, H) * 0.4;
    return { W, H, CX: W / 2, CY: H / 2, ORBIT_R };
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      onReveal();
      onComplete();
      return;
    }

    const timers = new Set<ReturnType<typeof setTimeout>>();
    const clones: HTMLElement[] = [];
    let done = false;
    const T = (fn: () => void, ms: number) => {
      const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
      timers.add(id);
      return id;
    };

    const ripple = (size: number, dur = 1400) => {
      const rings = ringsRef.current;
      if (!rings) return;
      const el = document.createElement("div");
      el.className = "bsli-ripple";
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.setProperty("--dur", `${dur}ms`);
      rings.appendChild(el);
      T(() => el.remove(), dur + 100);
    };

    const finish = () => {
      if (done) return;
      done = true;
      const stage = stageRef.current;
      if (stage) {
        stage.style.transition = "opacity 400ms ease";
        stage.style.opacity = "0";
      }
      T(onComplete, 420);
    };

    const flipToRoller = () => {
      const items = getRollerItems();
      LOGIN_AGENTS.forEach((a, i) => {
        const node = nodeRefs.current[i];
        const nodeSvg = node?.querySelector("svg");
        const item = items[i];
        if (!node || !nodeSvg || !item) return;

        const nodeRect = nodeSvg.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const sz = svgSize(a.viewBox, 13);

        const clone = document.createElement("div");
        clone.className = "bsli-flip-clone";
        clone.style.left = `${nodeRect.left}px`;
        clone.style.top = `${nodeRect.top + nodeRect.height / 2 - 6.5}px`;
        clone.style.width = `${sz.w}px`;
        clone.innerHTML = `<svg width="${sz.w}" height="13" viewBox="${a.viewBox}" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="${a.path}" fill="#46A0EB"/></svg>`;
        document.body.appendChild(clone);
        clones.push(clone);

        const dx = itemRect.left + 14 - nodeRect.left;
        const dy = itemRect.top + itemRect.height / 2 - 6.5 - (nodeRect.top + nodeRect.height / 2 - 6.5);

        T(() => {
          node.classList.remove("show");
          node.classList.add("hide");
          requestAnimationFrame(() => {
            clone.classList.add("flying");
            clone.style.transform = `translate(${dx}px, ${dy}px)`;
            clone.style.opacity = "0";
          });
          T(() => {
            // Land: reveal the roller item and tint its wordmark (first = active accent).
            item.style.opacity = "1";
            const p = item.querySelector("path");
            if (p) p.setAttribute("fill", i === 0 ? "#46A0EB" : "#1778C7");
            clone.remove();
            if (i === LOGIN_AGENTS.length - 1) T(finish, 200);
          }, 540);
        }, i * 80);
      });
    };

    // ── Timeline (ms) ──
    T(() => dotRef.current?.classList.add("alive"), 300);

    const diag = Math.sqrt(geom.W * geom.W + geom.H * geom.H);
    [120, 200, 300, 430, 580, 740, 920, diag * 0.7, diag * 0.9, diag * 1.05]
      .forEach((s, i) => T(() => ripple(s, 1600), 400 + i * 180));

    T(() => {
      dotRef.current?.classList.remove("alive");
      dotRef.current?.classList.add("expand");
    }, 1800);

    T(() => logoRef.current?.classList.add("show"), 2400);

    T(() => {
      orbitRef.current?.classList.add("show");
      nodeRefs.current.forEach((n, i) => T(() => n?.classList.add("show"), i * 100));
    }, 3200);

    T(() => {
      logoRef.current?.classList.remove("show");
      logoRef.current?.classList.add("hide");
      orbitRef.current?.classList.remove("show");
      orbitRef.current?.classList.add("hide");
      onReveal();
      // Let the sidebar/roller settle into place before measuring FLIP targets.
      T(flipToRoller, 250);
    }, 4700);

    // Safety net: never strand the user on the stage.
    T(finish, 9000);

    return () => {
      timers.forEach(clearTimeout);
      timers.clear();
      clones.forEach(c => c.remove());
      if (ringsRef.current) ringsRef.current.innerHTML = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; callbacks are stable for the sequence
  }, []);

  return (
    <>
      {introStyles}
      <Box ref={stageRef} sx={{ position: "fixed", inset: 0, zIndex: 10, pointerEvents: "none", overflow: "visible" }}>
        <Box ref={dotRef} className="bsli-dot" />
        <Box ref={ringsRef} sx={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
        <Box ref={orbitRef} id="bsli-orbit" style={{ width: geom.ORBIT_R * 2, height: geom.ORBIT_R * 2 }} />

        {/* Orbit nodes (agent wordmarks) */}
        {LOGIN_AGENTS.map((a, i) => {
          const angle = (i / LOGIN_AGENTS.length) * Math.PI * 2 - Math.PI / 2;
          const x = geom.CX + Math.cos(angle) * geom.ORBIT_R;
          const y = geom.CY + Math.sin(angle) * geom.ORBIT_R;
          const sz = svgSize(a.viewBox, 13);
          return (
            <div
              key={a.id}
              ref={el => { nodeRefs.current[i] = el; }}
              className="bsli-anode"
              style={{ left: x, top: y }}
            >
              <div className="bsli-anode-pip" />
              <svg width={sz.w} height={sz.h} viewBox={a.viewBox} fill="none" xmlns="http://www.w3.org/2000/svg" style={{ filter: "drop-shadow(0 0 5px rgba(70,160,235,0.5))" }}>
                <path d={a.path} fill="#46A0EB" />
              </svg>
            </div>
          );
        })}

        <Box ref={logoRef} id="bsli-stage-logo">
          <TheBprLogo fill="#1778C7" width="100%" />
        </Box>
      </Box>
    </>
  );
};

export default LoginIntroAnimation;
